import { useMemo, useRef } from 'react';

import { useParams } from 'react-router';
import { useMount } from 'react-use';

import { Button, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import CpuIcon from '@/components/Icons/CpuIcon';
import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import Loading from '@/components/Loading';
import { links } from '@/localization';
import { WorkspaceModelFragment } from '@/sections/Overview/Models/__generated__/fragments.generated';
import { useModels } from '@/sections/Overview/Models/hooks';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';

import { BASE_FUSE_SEARCH_OPTIONS, mapFilterToTitle, mapSortByToTitle } from '../constants';
import {
  useFilterQuery,
  useFilterSortBy,
  useFilterStatus,
  useTableContainerHeight,
  useViewMode,
} from '../hooks';
import EmptyState from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { Filter, MatchParams, SortBy, WorkspaceSections } from '../types';
import { filterModelsByStatus } from '../utils';
import ModelsEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { getModelsWithVersions, sortModels } from './utils';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function Models() {
  const { teamId } = useParams<MatchParams>();
  const { models, loading, refetch } = useModels(true, teamId);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.Models);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Models);
  const { filterStatus, setFilterStatus } = useFilterStatus(WorkspaceSections.Models);
  const { filterSortBy, setFilterSortBy } = useFilterSortBy(WorkspaceSections.Models);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = useTableContainerHeight(containerRef, Number(loading));

  useMount(() => {
    refetch();
  });

  const sortedModels = useMemo(() => {
    const modelsWithVersions = getModelsWithVersions(models);

    return sortModels(modelsWithVersions, filterSortBy as SortBy);
  }, [models, filterSortBy]);

  const filteredModels = useMemo(() => {
    const filteredByStatus = filterModelsByStatus(sortedModels, filterStatus);

    if (!filterQuery) {
      return filteredByStatus;
    }

    const fuse = new Fuse<WorkspaceModelFragment>(filteredByStatus, FUSE_SEARCH_OPTIONS);
    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, sortedModels, filterStatus]);

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(models)) {
      return <ModelsEmptyState />;
    }

    if (isEmpty(filteredModels)) {
      return <EmptyState>No Models found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid models={filteredModels} />
    ) : (
      <Table height={tableContainerHeight} models={filteredModels} />
    );
  }

  return (
    <Stack height="100%">
      <Stack gap={3} px={4} pt={4} pb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer>
              <CpuIcon />
            </IconContainer>
            <Typography variant="h2">Models</Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ExternalLinkIcon />}
            href={links.basetenDocsDeployingYourFirstModel}
            target="_blank"
          >
            Deploy a model
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Search query={filterQuery} setQuery={setFilterQuery} />
          <ListOptions<SortBy>
            title="Sort"
            options={Object.values(SortBy)}
            selectedOption={filterSortBy as SortBy}
            onOptionSelect={setFilterSortBy}
            mapOptionToTitle={(option) => mapSortByToTitle[option]}
          />
          <ListOptions<Filter>
            title="Status"
            options={Object.values(Filter)}
            selectedOption={filterStatus}
            onOptionSelect={setFilterStatus}
            mapOptionToTitle={(option) => mapFilterToTitle[option]}
          />
          <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
        </Stack>
      </Stack>
      <Stack flex={1} ref={containerRef} overflow="hidden">
        {getContent()}
      </Stack>
    </Stack>
  );
}

export default Models;
